import { type FormEvent, useEffect, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ColumnDialogProps = {
  initialName?: string;
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
};

export const ColumnDialog = ({ initialName, open, onClose, onSave }: ColumnDialogProps) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName ?? "");
    setError(null);
  }, [initialName, open]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(name);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The column could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog onClose={onClose} open={open} title={initialName ? "Rename column" : "New column"}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {error ? <Alert className="border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">{error}</Alert> : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="column-name">Column name</Label>
          <Input
            autoFocus
            id="column-name"
            maxLength={60}
            name="name"
            onChange={(event) => setName(event.target.value)}
            placeholder="Ready for QA"
            required
            value={name}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} type="button" variant="ghost">
            Cancel
          </Button>
          <Button disabled={saving} type="submit">
            {saving ? "Saving" : "Save column"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
